import type { ChatMessage } from '../features/chat/chat.types';
import { isMeaninglessToolName } from '../utils/toolHistory';

const MARKETING_CHAT_URL = `${process.env.REACT_APP_CHAT_API_BASE_URL || 'https://marketing-chatapi-stage.momentumamp.com'}/api/chat`;
const AUTH_CHAT_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams`;
const AUTH_NON_AMS_CHAT_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/non-ams`;
const NON_LOGIN_CHAT_URL = `${process.env.REACT_APP_MyAMP_Url}/api/non-login-chat`;
const API_KEY = 'myamp_live_9f3a7c2e8b4d6a1c5e9f0a2b7d4c8e6f';


const URL_TO_AI_SERVICE: Record<string, string> = {
  '/chat': 'SUPPORT_ASSISTANT',
  '/renewal-chat': 'AMS-RENEWAL-CHAT',
  '/lead-chat': 'AMS-LEAD-CHAT',
  '/coi-assistant': 'AMS-COI-ASSISTANT-CHAT',
  '/commission-chat': 'AMS-COMMISSION-CHAT',
  '/driving-license-chat': 'AMS-DRIVING-LICENSE-CHAT',
  '/call-assistant': 'AMS-CALL-ASSISTANT-CHAT',
  '/image-generation-chat': 'AMS-IMAGE-GENERATION-CHAT',
  '/report-chat': 'AMS-REPORT-CHAT',
  '/policy-verification-chat': 'AMS-POLICY-VERIFICATION-CHAT',
  '/data-import-chat': 'AMS-DATA-IMPORT-CHAT',
  '/trucking-chat': 'TRUCKING_APPETITE',
  '/carrier-finder-chat': 'CARRIER_FINDER',
  '/acord-form-chat': 'ACORD_FORMS_CHAT',
  '/rate-quoting-import-chat': 'AMS-RATE-QUOTING-IMPORT-CHAT',
};


const REQUIRES_CONFIRMATION = new Set([
  '/renewal-chat',
  '/lead-chat',
  '/coi-assistant',
  '/commission-chat',
  '/driving-license-chat',
]);

function parseFollowups(text: string): { message: string; followups: string[] } {
  const start = '$$FollowupQuestions$$';
  const end = '$$FollowupQuestionsEND$$';

  if (!text.includes(start)) {
    return { message: text.trim(), followups: [] };
  }

  const [main, rest] = text.split(start);
  const followupBlock = rest.split(end)[0];

  const followups = followupBlock
    .replace(/\r?\n/g, ' ')
    .split(/\d+\.\s+/)
    .map((q) => q.trim())
    .filter((q) => q.length > 0);

  return { message: main.trim(), followups };
}

export function getLastImageUrl(messages: ChatMessage[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const imageURL = messages[i].metadata?.imageURL;
    if (imageURL && imageURL.length > 0) {
      return imageURL[imageURL.length - 1];
    }
  }
  return undefined;
}

export interface PlanStep {
  step: number;
  title: string;
  tool: string;
  reason: string;
  status: 'pending' | 'active' | 'complete';
  resultSummary: string;
}

export interface ToolLogMeta {
  input?: string;
  output?: string;
  complete?: boolean;
}

export interface StreamCallbacks {
  onThought?: (thoughtSoFar: string, currentTool?: string, currentToolLabel?: string, toolMeta?: ToolLogMeta) => void;
  onChunk?: (textSoFar: string) => void;
  onDone?: (finalMessage: ChatMessage) => void;
  onPlanSteps?: (steps: PlanStep[], phase: string, stepNumber: number, totalSteps: number) => void;
}

/**
 * Send a chat message to the API.
 * @param content - The user message text
 * @param chatUrl - API endpoint path from the AMP's chatUrl field (e.g. '/chat', '/lead-chat')
 * @param callbacks - Streaming callbacks for thought/chunk/done events
 * @param conversationId - Conversation GUID sent to the API for thread tracking
 * @param filePaths - Optional array of uploaded file URLs to send with the message
 */
export async function sendMessage(
  content: string,
  chatUrl: string,
  callbacks?: StreamCallbacks,
  conversationId?: string,
  filePaths?: { fileName: string; fileURL: string }[],
  ampId?: string,
  channelId?: string,
  lastImageUrl?: string
): Promise<ChatMessage> {
  const authToken = localStorage.getItem('authToken');
  const isAuthenticated = !!authToken;

  const normalizedUrl = chatUrl.startsWith('/') ? chatUrl : `/${chatUrl}`;

  const formData = new FormData();
  formData.append('human_prompt', content);
  formData.append('conversationId', conversationId || 'default');
  formData.append('channel_id', channelId || '');

  const cleanAmpId = ampId?.startsWith('custom:') ? ampId.replace('custom:', '') : (ampId || '');
  formData.append('amp_id', cleanAmpId);

  const filePathsArray = filePaths && filePaths.length > 0 ? filePaths : [];
  formData.append('file_paths', JSON.stringify(filePathsArray));

  let url: string;
  let headers: Record<string, string>;

  if (normalizedUrl == '/image-generation-chat' && lastImageUrl) {
    formData.append('last_image_url', lastImageUrl);
  }

  if (isAuthenticated) {
    const base = (localStorage.getItem('isAMSUser') === 'true' || normalizedUrl == '/image-generation-chat')
      ? AUTH_CHAT_BASE
      : AUTH_NON_AMS_CHAT_BASE;

    // channel-chat is only for actual channel-scoped conversations (channelId
    // present); everything else keeps hitting its own skill-specific endpoint
    // (e.g. /chat, /lead-chat) like before.
    url = channelId ? `${base}/channel-chat` : `${base}${normalizedUrl}`;

    headers = {
      Authorization: `Bearer ${authToken}`,
      Accept: 'text/event-stream',
    };
    // ai_service is best-effort — send the mapped value if we have one for this
    // chat context, otherwise leave it empty rather than guessing.
    formData.append('ai_service', URL_TO_AI_SERVICE[normalizedUrl] || '');
    formData.append('nc_token', localStorage.getItem('amsToken') || '');
    formData.append('rate_token', localStorage.getItem('rateToken') || '');
    formData.append('nowcerts_ams_url', localStorage.getItem('agencyDomain') || '');
    if (REQUIRES_CONFIRMATION.has(normalizedUrl)) {
      formData.append('user_confirmation', 'true');
    }
  } else {
    url = NON_LOGIN_CHAT_URL;
    headers = {
      'X-API-KEY': API_KEY,
      Accept: 'text/event-stream',
    };
    formData.append('ai_service', 'NON-LOGIN-CHAT');
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      return {} as ChatMessage;
    }

    if (!response.ok || !response.body) {
      throw new Error('Failed to get response');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullText = '';
    let thoughtText = '';
    let chartJsData = '';
    let imageURLs: string[] = [];
    let responseChatId = '';
    let responseRunId = '';
    let aiToolNames: string[] = [];

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Check for plain JSON error response (not SSE-formatted)
      try {
        const rawParsed = JSON.parse(buffer.trim());
        if (rawParsed?.errorMessage === 'Invalid token') {
          reader.cancel();
          return {} as ChatMessage;
        }
      } catch {
        // Not plain JSON, continue with SSE parsing
      }

      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const event of events) {
        if (!event.startsWith('data:')) continue;

        const payload = event.replace('data:', '').trim();
        if (!payload) continue;

        try {
          const parsed: {
            type?: string;
            thought?: string;
            currentTool?: string;
            currentToolLabel?: string;
            Answer?: string;
            ResponseLoading?: boolean;
            response?: string;
            content?: string;
            error_message?: string;
            responseStreaming?: boolean;
            log?: boolean;
            uiThought?: string;
            uiLog?: string;
            planSteps?: PlanStep[];
            phase?: string;
            stepNumber?: number;
            totalSteps?: number;
            chartJsData?: string;
            imageURL?: string[];
            errorMessage?: string;
            chatId?: string;
            chat_id?: string;
            runId?: string;
            toolInput?: string;
            toolOutput?: string;
            toolComplete?: boolean;
            ai_tool?: string[];
          } = JSON.parse(payload);

          if (parsed.errorMessage === 'Invalid token') {
            reader.cancel();
            return {} as ChatMessage;
          }

          // Capture chatId when it appears in any event
          if (parsed.chatId || parsed.chat_id) {
            responseChatId = parsed.chatId || parsed.chat_id || '';
          }

          // Capture runId when it appears (rate quoting trigger)
          if (parsed.runId) {
            responseRunId = parsed.runId;
          }

          // Capture chartJsData when it appears (usually on the final event)
          if (parsed.chartJsData) {
            chartJsData = parsed.chartJsData;
          }

          // Capture imageURL when it appears (image generation responses)
          if (parsed.imageURL && Array.isArray(parsed.imageURL) && parsed.imageURL.length > 0) {
            imageURLs = parsed.imageURL;
          }

          // Capture ai_tool when it appears (e.g. image-generation-chat's "image_generate_tool")
          if (parsed.ai_tool && Array.isArray(parsed.ai_tool)) {
            const realNames = parsed.ai_tool.filter(n => !isMeaninglessToolName(n));
            if (realNames.length > 0) aiToolNames = realNames;
          }

          if (parsed.type === 'thought') {
            if (parsed.thought) thoughtText += parsed.thought;
            const meta: ToolLogMeta | undefined =
              (parsed.toolInput || parsed.toolOutput || parsed.toolComplete)
                ? { input: parsed.toolInput, output: parsed.toolOutput, complete: parsed.toolComplete }
                : undefined;
            callbacks?.onThought?.(thoughtText, parsed.currentTool, parsed.currentToolLabel, meta);
            if (parsed.planSteps) {
              callbacks?.onPlanSteps?.(parsed.planSteps, parsed.phase || '', parsed.stepNumber || 0, parsed.totalSteps || 0);
            }
            continue;
          }

          if (parsed.log === true) {
            const thought = parsed.uiThought || parsed.uiLog || '';
            if (thought) {
              thoughtText += thought + '\n';
              callbacks?.onThought?.(thoughtText, parsed.uiLog);
            }
            continue;
          }

          if (parsed.type === 'answer' || parsed.Answer !== undefined) {
            const chunk = parsed.Answer ?? parsed.response;
            if (chunk) {
              fullText += chunk;
              callbacks?.onChunk?.(fullText);
            } else if (parsed.error_message) {
              fullText = parsed.error_message;
              callbacks?.onChunk?.(fullText);
            }
          } else if (parsed.response && parsed.responseStreaming === true) {
            fullText += parsed.response;
            callbacks?.onChunk?.(fullText);
          } else if (parsed.content) {
            fullText += parsed.content;
            callbacks?.onChunk?.(fullText);
          }

          if (parsed.ResponseLoading === false || parsed.responseStreaming === false) {
            reader.cancel();
            break;
          }
        } catch {
        }
      }
    }

    const { message } = parseFollowups(fullText);

    const finalMsg: ChatMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: message || fullText || "I couldn't process that request. Please try again.",
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
      metadata: {
        ...(thoughtText ? { thoughtText } : {}),
        ...(chartJsData ? { chartJsData } : {}),
        ...(imageURLs.length > 0 ? { imageURL: imageURLs } : {}),
        ...(responseChatId ? { chatId: responseChatId } : {}),
        ...(responseRunId ? { runId: responseRunId } : {}),
        ...(aiToolNames.length > 0 ? { aiToolName: aiToolNames } : {}),
      },
    };

    callbacks?.onDone?.(finalMsg);
    return finalMsg;
  } catch {
    const errorMsg: ChatMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: 'Something went wrong. Please try again.',
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
    callbacks?.onDone?.(errorMsg);
    return errorMsg;
  }
}

/**
 * Send a message to the Marketing/Welcome channel chat API.
 * POST https://marketing-chatapi-stage.momentumamp.com/api/chat
 * Body: { question, conversation_id, chat_id }
 * Simulates SSE streaming so the response renders word-by-word like other chats.
 */
export async function sendMarketingMessage(
  content: string,
  conversationId: string,
  callbacks?: StreamCallbacks,
  filePaths?: { fileName: string; fileURL: string }[]
): Promise<ChatMessage> {
  const chatId = crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  try {
    const response = await fetch(MARKETING_CHAT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question: content,
        conversation_id: conversationId,
        chat_id: chatId,
        file_paths: JSON.stringify(filePaths),
      }),
    });

    if (!response.ok) throw new Error('Marketing chat API error');

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';
    let buffer = '';

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep last (potentially incomplete) line in buffer
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;

          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.type === 'answer') {
              if (parsed.Answer) {
                accumulated += parsed.Answer;
                callbacks?.onChunk?.(accumulated);
              }
              if (parsed.ResponseLoading === false) {
                reader.cancel();
                break;
              }
            }
          } catch {
            // skip malformed line
          }
        }
      }
    }

    const text = accumulated || "I couldn't process that request. Please try again.";

    const finalMsg: ChatMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: text,
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };

    callbacks?.onDone?.(finalMsg);
    return finalMsg;
  } catch {
    const errorMsg: ChatMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: 'Something went wrong. Please try again.',
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
    callbacks?.onDone?.(errorMsg);
    return errorMsg;
  }
}
